import App from '@/App';
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import ProtectedRoute from '@/components/ProtectedRoute/ProtectedRoute';
import Spinner from '@/components/Spinner/Spinner';
import AdminRoute from '@/features/ai/admin/AdminRoute';
import LoginPage from '@/features/auth/LoginPage';
import CollectionPage from '@/features/collection/CollectionPage';
import DeckEditorPage from '@/features/decks/DeckEditorPage';
import DecksPage from '@/features/decks/DecksPage';
import DraftPage from '@/features/draft/DraftPage';
import DeckNeedsPage from '@/features/lists/DeckNeedsPage';
import ListDetailPage from '@/features/lists/ListDetailPage';
import ListsPage from '@/features/lists/ListsPage';
import SetDetailsPage from '@/features/sets/SetDetailsPage';
import SetsPage from '@/features/sets/SetsPage';
import StarredPage from '@/features/starred/StarredPage';
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

// Lazy-loaded: the companion is a standalone immersive surface, so splitting it
// into its own chunk keeps the main bundle small (and under the PWA cache limit).
const GameCompanionPage = lazy(() => import('@/features/companion/GameCompanionPage'));

// Lazy-loaded: pulls in Prism for JSON highlighting, which only admins ever need.
const AiLogPage = lazy(() => import('@/features/ai/admin/AiLogPage'));

function CompanionFallback() {
  return (
    <div className="bg-void flex h-dvh w-screen items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

function RouteFallback() {
  return (
    <div className="flex h-full items-center justify-center py-16">
      <Spinner size="lg" />
    </div>
  );
}

const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    // Immersive, chrome-free surface (no AppLayout) for the table-top companion.
    // Public: the companion never needs an account, so it skips ProtectedRoute.
    path: '/companion',
    element: (
      <Suspense fallback={<CompanionFallback />}>
        <GameCompanionPage />
      </Suspense>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <App />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      { index: true, element: <CollectionPage /> },
      { path: 'starred', element: <StarredPage /> },
      { path: 'sets', element: <SetsPage /> },
      { path: 'sets/:setCode', element: <SetDetailsPage /> },
      { path: 'decks', element: <DecksPage /> },
      { path: 'decks/:deckId', element: <DeckEditorPage /> },
      { path: 'lists', element: <ListsPage /> },
      { path: 'lists/deck-needs', element: <DeckNeedsPage /> },
      { path: 'lists/:listId', element: <ListDetailPage /> },
      { path: 'draft', element: <DraftPage /> },
      {
        path: 'admin/ai-log',
        element: (
          <AdminRoute>
            <Suspense fallback={<RouteFallback />}>
              <AiLogPage />
            </Suspense>
          </AdminRoute>
        ),
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);

export default router;
